import { useEffect, useState, type ReactNode } from 'react';
import { ArrowUpRight, Facebook, Instagram, MapPin, Menu, X } from 'lucide-react';
import { logoHorizontal, logoVertical } from '../assets';
import { churchAddress, facebookUrl, instagramUrl, mapUrl, navigation } from '../site';
import { useScrolled } from '../hooks';

function normalize(path: string) {
  if (path === '/' || path === '/index.html') return '/';
  return path.endsWith('/') ? path : `${path}/`;
}

function currentPath() {
  return normalize(window.location.pathname);
}

function isCurrent(href: string, path: string) {
  if (href === '/') return path === '/';
  return path === href || path.startsWith(href);
}

export function SiteHeader() {
  const scrolled = useScrolled();
  const [menuOpen, setMenuOpen] = useState(false);
  const path = currentPath();

  useEffect(() => {
    document.body.classList.toggle('menu-is-open', menuOpen);
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.classList.remove('menu-is-open');
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [menuOpen]);

  return (
    <>
      <header className={`site-header ${scrolled ? 'is-scrolled' : ''}`}>
        <div className="page-shell header-inner">
          <a className="brand" href="/" aria-label="Tercer Cielo, volver al inicio">
            <img
              src={logoHorizontal}
              alt="Tercer Cielo Iglesia Cristiana"
              width="1102"
              height="297"
            />
          </a>

          <nav className="desktop-nav" aria-label="Navegación principal">
            {navigation.map((item) => (
              <a
                key={item.href}
                href={item.href}
                aria-current={isCurrent(item.href, path) ? 'page' : undefined}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <a className="button button-gold header-cta" href="/visita/">
            Planifica tu visita
          </a>
          <button
            className="menu-toggle"
            type="button"
            aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </button>
        </div>
      </header>

      <div
        className={`mobile-navigation ${menuOpen ? 'is-open' : ''}`}
        id="mobile-navigation"
        aria-hidden={!menuOpen}
      >
        <nav aria-label="Navegación móvil">
          {navigation.map((item, index) => (
            <a
              key={item.href}
              href={item.href}
              tabIndex={menuOpen ? 0 : -1}
              aria-current={isCurrent(item.href, path) ? 'page' : undefined}
            >
              <span>0{index + 1}</span>
              {item.label}
            </a>
          ))}
        </nav>
        <div className="mobile-menu-footer">
          <a
            href={facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            tabIndex={menuOpen ? 0 : -1}
          >
            <Facebook size={20} aria-hidden="true" /> Síguenos en Facebook
          </a>
          <a
            href={instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            tabIndex={menuOpen ? 0 : -1}
          >
            <Instagram size={20} aria-hidden="true" /> Síguenos en Instagram
          </a>
          <a href={mapUrl} target="_blank" rel="noopener noreferrer" tabIndex={menuOpen ? 0 : -1}>
            <MapPin size={20} aria-hidden="true" /> {churchAddress.street}
          </a>
          <p>{churchAddress.locality}</p>
        </div>
      </div>
    </>
  );
}

export function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="page-shell footer-main">
        <div className="footer-brand">
          <a href="/" aria-label="Tercer Cielo, volver al inicio">
            <img src={logoVertical} alt="Tercer Cielo Iglesia Cristiana" width="766" height="552" />
          </a>
          <p>Fe, familia y propósito en Ciudad Juárez.</p>
          <div className="footer-social" aria-label="Redes sociales">
            <a
              href={facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook de Tercer Cielo"
            >
              <Facebook size={20} aria-hidden="true" />
            </a>
            <a
              href={instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram de Tercer Cielo"
            >
              <Instagram size={20} aria-hidden="true" />
            </a>
          </div>
        </div>
        <div className="footer-column">
          <strong>Explora</strong>
          {navigation.slice(1).map((item) => (
            <a href={item.href} key={item.href}>
              {item.label}
            </a>
          ))}
        </div>
        <div className="footer-column">
          <strong>Encuéntranos</strong>
          <a href={facebookUrl} target="_blank" rel="noopener noreferrer">
            Facebook <ArrowUpRight size={14} aria-hidden="true" />
          </a>
          <a href={instagramUrl} target="_blank" rel="noopener noreferrer">
            Instagram <ArrowUpRight size={14} aria-hidden="true" />
          </a>
          <a href={mapUrl} target="_blank" rel="noopener noreferrer">
            {churchAddress.street}
          </a>
          <p>{churchAddress.locality}</p>
        </div>
      </div>
      <div className="page-shell footer-bottom">
        <p>© {new Date().getFullYear()} Iglesia Cristiana Tercer Cielo.</p>
        <p>Por un avivamiento del cielo en los corazones.</p>
      </div>
    </footer>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  return (
    <>
      <a className="skip-link" href="#main-content">
        Saltar al contenido principal
      </a>
      <SiteHeader />
      <main id="main-content">{children}</main>
      <SiteFooter />
    </>
  );
}
